import { projects } from "@folio-2026/content"
import Experience from "@folio-2026/experience"
import { EVENTS } from "@folio-2026/shared"
import assert from "assert"
import gsap from "gsap"
import { type GLTF } from "three/examples/jsm/Addons.js"
import * as THREE from "three/webgpu"
import type { FolderApi } from "tweakpane"

import { flashPattern } from "./const"

type RoomProps = {
  resources: {
    [key: string]: GLTF
  }
  scene: THREE.Scene
  debugFolder?: FolderApi
}

export default class Room {
  experience: Experience
  scene: THREE.Scene
  worksScene: THREE.Group
  debugFolder?: FolderApi

  pointLight: THREE.PointLight

  flashTimeline!: gsap.core.Timeline
  colorTween?: gsap.core.Tween

  constructor({ resources, scene, debugFolder }: RoomProps) {
    this.experience = Experience.getInstance()

    this.scene = scene
    this.debugFolder = debugFolder
    this.worksScene = resources.blender.scene.getObjectByName(
      "Works",
    ) as THREE.Group

    assert(
      this.worksScene,
      "Works scene not found. Did you forget to add it to un-hide the object in Blender?",
    )

    this.scene.add(this.worksScene)

    const lights = this.worksScene.getObjectByName("lights") as THREE.Mesh<
      THREE.BufferGeometry,
      THREE.MeshPhysicalNodeMaterial
    >
    lights.material.emissiveIntensity = 0.05

    const smoke = this.worksScene.getObjectByName("smoke") as THREE.Mesh<
      THREE.BufferGeometry,
      THREE.MeshPhysicalNodeMaterial
    >
    smoke.visible = false

    // Add Ambient Light

    // this.ambientLight = new THREE.AmbientLight(0x085775, 0.8)
    // this.scene.add(this.ambientLight)

    // Add Point Light
    this.pointLight = new THREE.PointLight(0x085775, 1)
    this.pointLight.position.set(0, 13, 0)
    this.scene.add(this.pointLight)

    this.setDebug()
    this.setFlashTimeline()

    this.setEventListeners()
  }

  setEventListeners() {
    this.experience.on(
      EVENTS.Works.BeforeEnter,
      this.beforeEnterAnimation.bind(this),
    )

    this.experience.on(
      EVENTS.Works.Entered,
      this.enterAnimationFinished.bind(this),
    )

    this.experience.on(EVENTS.Works.SlideEnter, (e: unknown) => {
      const { index } = e as { index: number }
      this.updatePointLightColor(index)
    })
  }

  updatePointLightColor(index: number) {
    // Kill any existing color animation
    if (this.colorTween) {
      this.colorTween.kill()
    }

    // Get current color RGB values
    const currentColor = this.pointLight.color
    const currentR = currentColor.r
    const currentG = currentColor.g
    const currentB = currentColor.b

    // Parse target color to get RGB values
    const targetColor = new THREE.Color(projects[index].color)
    const targetR = targetColor.r
    const targetG = targetColor.g
    const targetB = targetColor.b

    // Create proxy object for animation
    const colorProxy = {
      r: currentR,
      g: currentG,
      b: currentB,
    }

    // Animate color transition over 1 second
    this.colorTween = gsap.to(colorProxy, {
      r: targetR,
      g: targetG,
      b: targetB,
      duration: 1,
      ease: "power2.inOut",
      onUpdate: () => {
        this.pointLight.color.setRGB(colorProxy.r, colorProxy.g, colorProxy.b)
      },
      onComplete: () => {
        this.colorTween = undefined
      },
    })
  }

  beforeEnterAnimation() {
    this.flashTimeline.seek(0).pause()
  }

  enterAnimationFinished() {
    this.flashTimeline.restart()
  }

  setFlashTimeline() {
    // Create a gsap timeline that flashes the pointLight in an irregular storm/blackout pattern
    // Add 0.7s initial delay to sync with Man's glowTimeline first tween
    this.flashTimeline = gsap.timeline({
      repeat: -1,
      paused: true,
    })

    for (let i = 0; i < flashPattern.length - 1; i++) {
      const [startTime] = flashPattern[i]
      const [nextTime, nextIntensity] = flashPattern[i + 1]
      this.flashTimeline.to(
        this.pointLight,
        {
          intensity: nextIntensity * 100,
          duration: nextTime - startTime,
          ease: "power2.inOut",
        },
        startTime, // Offset by 0.7s to sync with Man's timeline
      )
    }
  }

  setDebug() {
    if (!this.debugFolder) {
      return
    }

    const lights = this.worksScene.getObjectByName("lights") as THREE.Mesh<
      THREE.BufferGeometry,
      THREE.MeshPhysicalNodeMaterial
    >

    const roomFolder = this.debugFolder.addFolder({
      title: "🏠 Room",
      expanded: false,
    })

    roomFolder.addBinding(lights.material, "emissiveIntensity", {
      label: "Lights Emissive Intensity",
      min: 0,
      max: 1,
      step: 0.01,
    })

    roomFolder.addBinding(this.pointLight, "intensity", {
      label: "Point Light Intensity",
      min: 0,
      max: 100,
      step: 0.01,
    })

    roomFolder.addBinding(this.pointLight, "color", {
      label: "Point Light Color",
    })

    roomFolder.addBinding(this.pointLight.position, "y", {
      label: "Point Light Y Position",
      min: -20,
      max: 20,
      step: 0.01,
    })

    roomFolder
      .addButton({
        title: "Start Flash Timeline",
      })
      .on("click", () => {
        this.flashTimeline.restart()
      })
  }
}
